import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  Input,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
} from "@material-ui/core";
import {
  Add as AddIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useTheme, alpha } from "@material-ui/core/styles";
import { orange } from "@material-ui/core/colors";
import { isDarkTheme } from "../../helpers/darkTint";
import { toast } from "react-toastify";
import { createCard, updateCard, getClientes, listRelatedCards as listRelatedCardsService, getEmpsisImobilizados } from "../../services/kanban";
import { showBoard } from "../../services/kanban";
import ImobilizadoAutocomplete, { isImobilizadoField } from "./ImobilizadoAutocomplete";
import { TIPO_LOCACAO_FIELD, DATA_DEVOLUCAO_FIELD, calcularDataDevolucao, isDevolucaoEditavel } from "../../helpers/locacaoDevolucao";
import MonetaryInput from "../MonetaryInput";
import ClienteComments from "./ClienteComments";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { requestFollowUpPrompt } from "../../helpers/followUpPrompt";
import ClienteQuickCreateModal from "../ClienteQuickCreateModal";


const CardModal = ({ open, onClose, card, column, boardId, board, onSuccess }) => {
  const history = useHistory();
  const isDark = isDarkTheme(useTheme());

  // A página já tem o board carregado (prop `board`). Os dois efeitos abaixo
  // chamavam showBoard a cada abertura, baixando o board inteiro (com todos os
  // cards) duas vezes. Ref em vez de dependência: um update do board via socket
  // com o modal aberto não pode rodar os efeitos de novo e resetar o formulário.
  const boardRef = useRef(board);
  boardRef.current = board;

  const getBoardData = async () => {
    const loadedBoard = boardRef.current;
    if (
      loadedBoard &&
      Number(loadedBoard.id) === Number(boardId) &&
      Array.isArray(loadedBoard.columns)
    ) {
      return loadedBoard;
    }
    const response = await showBoard(boardId);
    return response.data;
  };
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({});
  const [fieldDefinitions, setFieldDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingFields, setLoadingFields] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [showAllFields, setShowAllFields] = useState(false);
  
  // Estados para Cliente
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [clienteSearchText, setClienteSearchText] = useState("");
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  // Estados para Usuário de Venda
  const [users, setUsers] = useState([]);
  const [selectedSaleUser, setSelectedSaleUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [hasRegisterSaleColumn, setHasRegisterSaleColumn] = useState(false);

  // Estados para agendamento obrigatório
  const [scheduleDate, setScheduleDate] = useState("");
  const [requireSchedule, setRequireSchedule] = useState(false);

  // Estado para sugestão de usuário responsável do cliente
  const [responsavelSuggestion, setResponsavelSuggestion] = useState(null);

  // Estados para importação de clientes
  const [isClientImportEnabled, setIsClientImportEnabled] = useState(false);
  const [importingClientes, setImportingClientes] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importProgress, setImportProgress] = useState({
    message: 'Iniciando...',
    percentual: 0
  });
  const [relatedCards, setRelatedCards] = useState([]);
  const [relatedNegotiationRef, setRelatedNegotiationRef] = useState(null);
  const [loadingRelatedCards, setLoadingRelatedCards] = useState(false);
  const clienteCommentsRef = useRef(null);

  // Descrições dos patrimônios já gravados no card, resolvidas numa chamada só
  // ao abrir o modal (a busca em si acontece dentro de cada autocomplete).
  const [imobilizadosResolvidos, setImobilizadosResolvidos] = useState({});

  // Limpar estados quando o modal fechar
  useEffect(() => {
    if (!open) {
      setSelectedCliente(null);
      setClienteSearchText("");
      setValidationErrors({});
      setSelectedSaleUser(null);
      setScheduleDate("");
      setShowAllFields(false);
      setRelatedCards([]);
      setRelatedNegotiationRef(null);
      setLoadingRelatedCards(false);
      setResponsavelSuggestion(null);
      setQuickCreateOpen(false);
    }
  }, [open]);

  // Resolve a descrição dos patrimônios que o card já tem gravados, numa única
  // chamada. Card sem patrimônio não consulta nada: a busca só roda quando o
  // usuário digita 3 letras dentro do autocomplete.
  useEffect(() => {
    if (!open) return;

    const codigos = fieldDefinitions
      .filter(isImobilizadoField)
      .map((field) => formData[field.fieldName])
      .filter((codigo) => codigo !== undefined && codigo !== null && String(codigo).trim() !== "")
      .map((codigo) => String(codigo).trim());

    const codigosUnicos = [...new Set(codigos)];
    if (codigosUnicos.length === 0) return;

    // Já resolvidos numa passada anterior: não vale outra chamada.
    if (codigosUnicos.every((codigo) => imobilizadosResolvidos[codigo])) return;

    let ativo = true;

    (async () => {
      try {
        const { data } = await getEmpsisImobilizados({ patrimonios: codigosUnicos });
        if (!ativo || !Array.isArray(data)) return;

        setImobilizadosResolvidos((prev) => {
          const proximo = { ...prev };
          data.forEach((item) => {
            proximo[item.patrimonio] = item;
          });
          return proximo;
        });
      } catch (err) {
        console.error("Erro ao resolver imobilizados da empsis:", err);
      }
    })();

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fieldDefinitions, formData]);

  useEffect(() => {
    const loadRelatedCards = async () => {
      if (!open || !card?.id) {
        setRelatedCards([]);
        setRelatedNegotiationRef(null);
        return;
      }

      try {
        setLoadingRelatedCards(true);
        const { data } = await listRelatedCardsService(card.id);
        setRelatedCards(Array.isArray(data?.cards) ? data.cards : []);
        setRelatedNegotiationRef(data?.negotiationRef || null);
      } catch (err) {
        console.error("Erro ao carregar cards relacionados:", err);
        setRelatedCards([]);
        setRelatedNegotiationRef(null);
      } finally {
        setLoadingRelatedCards(false);
      }
    };

    loadRelatedCards();
  }, [open, card?.id]);

  // Carregar configuração de agendamento obrigatório e importação de clientes
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        const scheduleSetting = data.find(s => s.key === "requireScheduleOnCardCreation");
        setRequireSchedule(scheduleSetting?.value === "enabled");
        
        const clientImportSetting = data.find(s => s.key === "clientImport");
        setIsClientImportEnabled(clientImportSetting?.value === "enabled");
      } catch (err) {
        console.error("Erro ao carregar configurações:", err);
        setRequireSchedule(false);
        setIsClientImportEnabled(false);
      }
    };

    if (open) {
      loadSettings();
    }
  }, [open]);

  // Carregar as definições de campos do board
  useEffect(() => {
    const loadFieldDefinitions = async () => {
      if (!boardId) return;
      
      try {
        setLoadingFields(true);
        const board = await getBoardData();

        // Campos obrigatórios que sempre devem existir
        const mandatoryFields = [
          {
            fieldName: "title",
            fieldLabel: "Título",
            fieldType: "text",
            isRequired: true,
            placeholder: "Digite o título do card"
          },
          {
            fieldName: "description",
            fieldLabel: "Descrição",
            fieldType: "textarea",
            isRequired: false,
            placeholder: "Descreva os detalhes do card"
          },
          {
            fieldName: "valorReais",
            fieldLabel: "Valor R$",
            fieldType: "money",
            isRequired: false,
            placeholder: "R$ 0,00"
          }
        ];
        
        if (board.fieldDefinitions && board.fieldDefinitions.length > 0) {
          // Normalizar fieldDefinitions (deserializar arrays de defaultValue)
          const normalizedFields = board.fieldDefinitions.map(field => {
            const normalizedField = {
              ...field,
              fieldOptions: Array.isArray(field.fieldOptions) 
                ? field.fieldOptions 
                : (field.fieldOptions ? [] : [])
            };
            
            // Deserializar defaultValue se for JSON string de array
            if ((field.fieldType === 'multiselect' || field.fieldType === 'checkbox') && 
                typeof field.defaultValue === 'string' && 
                field.defaultValue.trim().startsWith('[')) {
              try {
                normalizedField.defaultValue = JSON.parse(field.defaultValue);
              } catch (e) {
                console.error('Erro ao fazer parse de defaultValue:', e);
                normalizedField.defaultValue = [];
              }
            }
            
            return normalizedField;
          });
          
          // Garantir que title, description e valorReais sempre estejam presentes
          const hasTitle = normalizedFields.some(f => f.fieldName === "title");
          const hasDescription = normalizedFields.some(f => f.fieldName === "description");
          const hasValorReais = normalizedFields.some(f => f.fieldName === "valorReais");
          
          let finalFields = [...normalizedFields];
          
          // Adicionar title no início se não existir
          if (!hasTitle) {
            finalFields.unshift(mandatoryFields[0]);
          }
          
          // Adicionar description após title se não existir
          if (!hasDescription) {
            const titleIndex = finalFields.findIndex(f => f.fieldName === "title");
            finalFields.splice(titleIndex + 1, 0, mandatoryFields[1]);
          }
          
          // Adicionar valorReais após description se não existir
          if (!hasValorReais) {
            const descIndex = finalFields.findIndex(f => f.fieldName === "description");
            finalFields.splice(descIndex + 1, 0, mandatoryFields[2]);
          }
          
          console.log('Field Definitions normalizados:', finalFields);
          setFieldDefinitions(finalFields);
        } else {
          console.log('Nenhum fieldDefinition encontrado, usando campos padrão');
          // Se não há definições, usar campos padrão completos
          setFieldDefinitions([
            ...mandatoryFields,
            {
              fieldName: "priority",
              fieldLabel: "Prioridade",
              fieldType: "select",
              isRequired: false,
              fieldOptions: [
                { value: "low", label: "Baixa" },
                { value: "medium", label: "Média" },
                { value: "high", label: "Alta" },
                { value: "urgent", label: "Urgente" }
              ],
              defaultValue: "medium"
            },
            {
              fieldName: "dueDate",
              fieldLabel: "Data de Vencimento",
              fieldType: "date",
              isRequired: false
            }
          ]);
        }
      } catch (err) {
        console.error("Erro ao carregar definições de campos:", err);
        toast.error("Erro ao carregar campos do board");
      } finally {
        setLoadingFields(false);
      }
    };

    if (open) {
      loadFieldDefinitions();
    }
    // getBoardData lê o board por ref de propósito (ver boardRef acima).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, open]);

  // Verificar se existe alguma coluna com registerSale no board e carregar usuários
  useEffect(() => {
    const checkRegisterSaleAndLoadUsers = async () => {
      if (!boardId || !open) return;

      try {
        const board = await getBoardData();
        
        // Verificar se existe alguma coluna com registerSale = true
        const hasRegisterSale = board.columns && board.columns.some(col => col.registerSale);
        setHasRegisterSaleColumn(hasRegisterSale);
        
        // Se existe, carregar usuários
        if (hasRegisterSale) {
          setLoadingUsers(true);
          const { data } = await api.get("/users/");
          setUsers(data.users || []);
          setLoadingUsers(false);
        }
      } catch (err) {
        console.error("Erro ao verificar colunas ou carregar usuários:", err);
        setLoadingUsers(false);
      }
    };

    checkRegisterSaleAndLoadUsers();
    // getBoardData lê o board por ref de propósito (ver boardRef acima).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, open]);

  // Definir usuário de venda quando card está sendo editado
  useEffect(() => {
    if (!open || !card) return;
    
    if (card.saleUserId && users.length > 0) {
      const user = users.find(u => u.id === card.saleUserId);
      if (user) {
        setSelectedSaleUser(user);
      }
    } else {
      setSelectedSaleUser(null);
    }
  }, [card, open, users]);

  // Carregar clientes
  useEffect(() => {
    const loadClientes = async () => {
      if (!open) return;
      
      try {
        setLoadingClientes(true);
        const response = await getClientes({ 
          searchParam: clienteSearchText,
          pageNumber: 1 
        });
        // Manter cliente selecionado se já existir na lista ou adicionar ao topo
        const newClientes = response.data.clientes || [];
        
        // Se há um cliente selecionado e ele não está na nova lista, adicionar
        if (selectedCliente && !newClientes.find(c => c.CODCLI === selectedCliente.CODCLI)) {
          setClientes([selectedCliente, ...newClientes]);
        } else {
          setClientes(newClientes);
        }
      } catch (err) {
        console.error("Erro ao carregar clientes:", err);
        // Manter cliente selecionado em caso de erro
        if (selectedCliente) {
          setClientes([selectedCliente]);
        }
      } finally {
        setLoadingClientes(false);
      }
    };

    const timeoutId = setTimeout(() => {
      loadClientes();
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [clienteSearchText, open]); // Removido selectedCliente das dependências para evitar loops

  // Definir cliente selecionado quando card está sendo editado
  useEffect(() => {
    if (!open) return;
    
    if (card && card.cliente) {
      setSelectedCliente(card.cliente);
    } else if (card && card.clienteId) {
      // Tentar encontrar o cliente na lista atual
      const cliente = clientes.find(c => c.CODCLI === card.clienteId);
      if (cliente) {
        setSelectedCliente(cliente);
      } else {
        // Se não encontrou o cliente, limpar
        setSelectedCliente(null);
      }
    } else {
      // Se não há card (novo card) OU o card não tem cliente, limpar seleção
      setSelectedCliente(null);
    }
  }, [card, open]); // Removido 'clientes' das dependências para evitar loops

  // Detectar usuário responsável do cliente selecionado e sugerir inclusão no campo userID
  useEffect(() => {
    if (card) {
      // Não exibir sugestão ao editar
      setResponsavelSuggestion(null);
      return;
    }

    if (!selectedCliente || !selectedCliente.USUARIO_RESPONSAVEL) {
      setResponsavelSuggestion(null);
      return;
    }

    const userIdField = fieldDefinitions.find(f => f.fieldName === "userID");
    if (!userIdField) {
      setResponsavelSuggestion(null);
      return;
    }

    const options = Array.isArray(userIdField.fieldOptions) ? userIdField.fieldOptions : [];
    const match = options.find(
      opt => String(opt.value) === String(selectedCliente.USUARIO_RESPONSAVEL)
    );

    if (match) {
      setResponsavelSuggestion({
        userId: match.value,
        userName: match.label,
        fieldLabel: userIdField.fieldLabel || "Usuário",
      });
    } else {
      setResponsavelSuggestion(null);
    }
  }, [selectedCliente, fieldDefinitions, card]);

  // Preencher formData com valores do card ou valores padrão
  useEffect(() => {
    if (fieldDefinitions.length === 0) return;

    console.log('=== CardModal: Preenchendo formulário ===');
    console.log('Card recebido:', card);
    console.log('Card.customFields (raw):', card?.customFields);
    console.log('Field Definitions:', fieldDefinitions);

    const initialData = {};
    
    // Função auxiliar para formatar data com segurança
    const formatDateValue = (dateValue) => {
      if (!dateValue) return "";
      try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "";
        return date.toISOString().split("T")[0];
      } catch (error) {
        console.error("Erro ao formatar data:", error);
        return "";
      }
    };
    
    // Parse customFields se vier como string JSON
    let parsedCustomFields = null;
    if (card && card.customFields) {
      if (typeof card.customFields === 'string') {
        try {
          parsedCustomFields = JSON.parse(card.customFields);
          console.log('CustomFields parseado:', parsedCustomFields);
        } catch (e) {
          console.error('Erro ao fazer parse de customFields:', e);
          parsedCustomFields = {};
        }
      } else {
        parsedCustomFields = card.customFields;
      }
    }
    
    const getFieldOptions = (field) => {
      if (!field.fieldOptions) return [];
      if (Array.isArray(field.fieldOptions)) return field.fieldOptions;
      if (typeof field.fieldOptions === "string") {
        try {
          const parsed = JSON.parse(field.fieldOptions);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error("Erro ao fazer parse de fieldOptions:", e);
          return [];
        }
      }
      if (typeof field.fieldOptions === "object") {
        return Object.values(field.fieldOptions);
      }
      return [];
    };

    const currentUserId = user?.id;

    fieldDefinitions.forEach((field) => {
      if (card) {
        // Se estamos editando, pegar valores do card
        if (parsedCustomFields && parsedCustomFields[field.fieldName] !== undefined) {
          const value = parsedCustomFields[field.fieldName];
          
          console.log(`Campo ${field.fieldName} encontrado em customFields:`, value);
          
          // Validar se o valor existe nas opções (para select e radio)
          if (field.fieldType === "select" || field.fieldType === "radio") {
            const options = field.fieldOptions || [];
            const validOptions = Array.isArray(options) ? options : [];
            const isValid = validOptions.some(opt => String(opt.value) === String(value));
            
            if (!isValid && value !== "") {
              console.warn(`Valor ${value} não é válido para campo ${field.fieldName}. Usando valor padrão.`);
              initialData[field.fieldName] = field.defaultValue || "";
            } else {
              initialData[field.fieldName] = value;
            }
          }
          // Tratar campos de seleção múltipla (devem ser arrays)
          else if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
            initialData[field.fieldName] = Array.isArray(value) ? value : [];
          }
          // Tratar datas especialmente
          else if (field.fieldType === "date" && value) {
            initialData[field.fieldName] = formatDateValue(value);
          } 
          // Tratar campos monetários - MonetaryInput espera valor decimal
          else if (field.fieldType === "money" && value) {
            // Converter string para número, se necessário
            const numValue = typeof value === 'string' ? parseFloat(value) : value;
            const finalValue = !isNaN(numValue) ? numValue : 0;
            console.log(`💰 Campo monetário ${field.fieldName}: valor original=${value}, convertido=${finalValue}`);
            initialData[field.fieldName] = finalValue;
          } else {
            initialData[field.fieldName] = value;
          }
        } else if (card[field.fieldName] !== undefined && card[field.fieldName] !== null && card[field.fieldName] !== "") {
          // Fallback para campos diretos do card (retrocompatibilidade)
          console.log(`Campo ${field.fieldName} encontrado diretamente no card:`, card[field.fieldName]);
          
          if (field.fieldType === "date" && card[field.fieldName]) {
            initialData[field.fieldName] = formatDateValue(card[field.fieldName]);
          } else if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
            initialData[field.fieldName] = Array.isArray(card[field.fieldName]) ? card[field.fieldName] : [];
          } else if (field.fieldType === "money" && card[field.fieldName]) {
            // Tratar campos monetários - MonetaryInput espera valor decimal
            const numValue = typeof card[field.fieldName] === 'string' ? parseFloat(card[field.fieldName]) : card[field.fieldName];
            const finalValue = !isNaN(numValue) ? numValue : 0;
            console.log(`💰 Campo monetário ${field.fieldName} (fallback): valor original=${card[field.fieldName]}, convertido=${finalValue}`);
            initialData[field.fieldName] = finalValue;
          } else {
            initialData[field.fieldName] = card[field.fieldName];
          }
        } else {
          // Valor padrão ou vazio
          console.log(`Campo ${field.fieldName} não encontrado, usando valor padrão`);
          
          if (field.fieldType === "date" && field.defaultValue) {
            initialData[field.fieldName] = formatDateValue(field.defaultValue);
          } else if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
            // Campos de seleção múltipla: usar defaultValue se existir, senão array vazio
            initialData[field.fieldName] = Array.isArray(field.defaultValue) 
              ? field.defaultValue 
              : [];
          } else {
            initialData[field.fieldName] = field.defaultValue || "";
          }
        }
      } else {
        // Se é novo card, usar valores padrão
        if (field.fieldType === "date" && field.defaultValue) {
          initialData[field.fieldName] = formatDateValue(field.defaultValue);
        } else if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
          // Campos de seleção múltipla: usar defaultValue se existir, senão array vazio
          initialData[field.fieldName] = Array.isArray(field.defaultValue) 
            ? field.defaultValue 
            : [];
        } else {
          initialData[field.fieldName] = field.defaultValue || "";
        }

        if (field.fieldName === "userID") {
          if (currentUserId !== undefined && currentUserId !== null) {
            const options = getFieldOptions(field);
            const matchedOption = options.find(
              (option) => String(option.value) === String(currentUserId)
            );
            initialData[field.fieldName] = matchedOption ? matchedOption.value : "";
          } else {
            initialData[field.fieldName] = "";
          }
        }
      }
    });

    console.log('Dados iniciais do formulário:', initialData);
    setFormData(initialData);
    setValidationErrors({}); // Limpar erros ao abrir/recarregar
  }, [card, fieldDefinitions, open]);

  // Card salvo antes desta regra pode ter o tipo de locação preenchido e a
  // devolução vazia. Como o campo agora fica desabilitado, preenchemos na
  // abertura — senão o obrigatório trava o save sem o usuário poder digitar.
  useEffect(() => {
    if (!open) return;

    const tipo = formData[TIPO_LOCACAO_FIELD];
    if (!tipo || formData[DATA_DEVOLUCAO_FIELD]) return;

    const calculada = calcularDataDevolucao(tipo, card?.createdAt);
    if (!calculada) return;

    setFormData((prev) =>
      prev[DATA_DEVOLUCAO_FIELD] ? prev : { ...prev, [DATA_DEVOLUCAO_FIELD]: calculada }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData[TIPO_LOCACAO_FIELD], formData[DATA_DEVOLUCAO_FIELD], card]);

  // Boards de locação: trocar o tipo recalcula a data de devolução a partir da
  // data de lançamento do card (createdAt; card novo usa hoje). O valor continua
  // editável — quem ajusta depois manda, porque o backend nunca sobrescreve.
  const aplicarDevolucaoAoTrocarTipo = (dados, fieldName, fieldValue) => {
    if (fieldName !== TIPO_LOCACAO_FIELD) return dados;

    const temCampoDevolucao = fieldDefinitions.some(
      (field) => field.fieldName === DATA_DEVOLUCAO_FIELD
    );
    if (!temCampoDevolucao) return dados;

    const novaData = calcularDataDevolucao(fieldValue, card?.createdAt);
    if (!novaData) return dados;

    return { ...dados, [DATA_DEVOLUCAO_FIELD]: novaData };
  };

  const handleFieldValueChange = (fieldName, fieldValue) => {
    setFormData((prev) =>
      aplicarDevolucaoAoTrocarTipo(
        { ...prev, [fieldName]: fieldValue },
        fieldName,
        fieldValue
      )
    );

    if (fieldName === "userID") {
      setResponsavelSuggestion(null);
    }

    // Limpar erro de validação ao começar a digitar. Quando o tipo de locação
    // preenche a devolução, o erro dela também sai.
    setValidationErrors((prev) => {
      if (!prev[fieldName] && !(fieldName === TIPO_LOCACAO_FIELD && prev[DATA_DEVOLUCAO_FIELD])) {
        return prev;
      }
      const next = { ...prev, [fieldName]: false };
      if (fieldName === TIPO_LOCACAO_FIELD) {
        next[DATA_DEVOLUCAO_FIELD] = false;
      }
      return next;
    });
  };

  const handleChange = (e) => {
    handleFieldValueChange(e.target.name, e.target.value);
  };

  // Handler especial para checkbox múltiplos
  const handleCheckboxChange = (fieldName, optionValue, isChecked) => {
    const currentValues = Array.isArray(formData[fieldName]) ? formData[fieldName] : [];
    
    let newValues;
    if (isChecked) {
      // Adicionar valor se marcado
      newValues = [...currentValues, optionValue];
    } else {
      // Remover valor se desmarcado
      newValues = currentValues.filter(v => v !== optionValue);
    }
    
    setFormData({
      ...formData,
      [fieldName]: newValues,
    });
    
    // Limpar erro de validação
    if (validationErrors[fieldName]) {
      setValidationErrors({
        ...validationErrors,
        [fieldName]: false,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar campos de agendamento obrigatórios APENAS para novos cards E se a configuração estiver ativa
    if (!card && requireSchedule) {
      if (!scheduleDate) {
        toast.error("Data do agendamento é obrigatória");
        return;
      }
      // Validar que descrição está preenchida (será usada como observação do agendamento)
      const description = formData.description;
      if (!description || description.trim() === "") {
        toast.error("Descrição é obrigatória para criar o agendamento");
        return;
      }
    }

    // Validar campos obrigatórios
    const errors = {};
    const missingFields = [];
    
    fieldDefinitions.forEach((field) => {
      const value = formData[field.fieldName];
      
      if (field.isRequired) {
        // Para campos de seleção múltipla (arrays)
        if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
          if (!Array.isArray(value) || value.length === 0) {
            errors[field.fieldName] = true;
            missingFields.push(field.fieldLabel);
          }
        }
        // Para campos de texto e outros
        else if (!value || (typeof value === 'string' && !value.trim())) {
          errors[field.fieldName] = true;
          missingFields.push(field.fieldLabel);
        }
      }
    });

    if (missingFields.length > 0) {
      setValidationErrors(errors);
      toast.error(`Campos obrigatórios não preenchidos: ${missingFields.join(", ")}`);
      return;
    }

    // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: ao salvar o card,
    // persistir também comentário pendente digitado no bloco de comentários.
    if (clienteCommentsRef.current?.hasPendingComment?.()) {
      const commentResult = await clienteCommentsRef.current.savePendingComment();
      if (!commentResult?.saved) {
        return;
      }
    }

    setLoading(true);

    try {
      const data = {
        customFields: { ...formData },
        clienteId: selectedCliente?.CODCLI || null,
        saleUserId: selectedSaleUser?.id || null,
        // Incluir dados de agendamento apenas para novos cards E se a configuração estiver ativa
        ...((!card && requireSchedule) && {
          scheduleDate
        })
        // tasks removido - agora gerenciado pelo TasksModal
      };

      console.log('=== Salvando card ===');
      console.log('Dados completos:', data);

      if (card) {
        // Editando card existente
        await updateCard(card.id, data);
        toast.success("Card atualizado com sucesso!");
      } else {
        // Criando novo card
        await createCard({
          ...data,
          columnId: column.id,
          boardId: boardId,
        });
        toast.success("Card criado com sucesso!");
      }

      onSuccess();
      onClose();

      if (!card) {
        await requestFollowUpPrompt(i18n.t("createKanbanCardModal.followUpSource"), {
          cliente: selectedCliente || null,
          clienteId: selectedCliente?.CODCLI || null,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Erro ao salvar card");
    } finally {
      setLoading(false);
    }
  };

  // O cadastro rápido não se fecha sozinho no sucesso — quem fecha é este
  // handler. Selecionar o cliente dispara o onInputChange do Autocomplete e,
  // com isso, uma nova busca em 300ms; o guard do efeito de carga mantém o
  // selecionado no topo da lista mesmo que a busca não o traga
  const handleClienteCreated = (novoCliente) => {
    setQuickCreateOpen(false);
    if (!novoCliente?.CODCLI) return;

    setClientes((current) =>
      current.some((c) => String(c.CODCLI) === String(novoCliente.CODCLI))
        ? current
        : [novoCliente, ...current]
    );
    setSelectedCliente(novoCliente);
  };

  // Função para importar/sincronizar clientes
  const handleImportClientes = async () => {
    try {
      setImportingClientes(true);
      setImportDialogOpen(true);
      setImportProgress({
        message: 'Iniciando importação...',
        percentual: 0
      });
      
      // Simular progresso enquanto importa
      const progressInterval = setInterval(() => {
        setImportProgress(prev => ({
          ...prev,
          percentual: prev.percentual < 90 ? prev.percentual + 10 : prev.percentual,
          message: prev.percentual < 30 ? 'Conectando com API...' :
                   prev.percentual < 60 ? 'Importando clientes...' :
                   prev.percentual < 90 ? 'Processando dados...' : 'Finalizando...'
        }));
      }, 500);
      
      await api.post("/clientes/importar");
      
      clearInterval(progressInterval);
      setImportProgress({
        message: 'Importação concluída!',
        percentual: 100
      });
      
      toast.success("Clientes importados com sucesso!");
      
      // Aguardar um pouco para mostrar 100%
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Recarregar a lista de clientes
      const response = await getClientes({ 
        searchParam: clienteSearchText,
        pageNumber: 1 
      });
      setClientes(response.data.clientes || []);
      
      setImportDialogOpen(false);
      
    } catch (err) {
      console.error("Erro ao importar clientes:", err);
      toast.error(err.response?.data?.message || "Erro ao importar clientes");
      setImportDialogOpen(false);
    } finally {
      setImportingClientes(false);
    }
  };

  const renderField = (field) => {
    const hasError = validationErrors[field.fieldName] || false;

    // Campos com fonte de dados externa não passam pelo switch de fieldType:
    // continuam sendo "text" no banco, mas a entrada é um autocomplete.
    if (isImobilizadoField(field)) {
      return (
        <ImobilizadoAutocomplete
          label={field.fieldLabel + (field.isRequired ? " *" : "")}
          value={formData[field.fieldName] || ""}
          onChange={(novoValor) => handleFieldValueChange(field.fieldName, novoValor)}
          opcaoResolvida={imobilizadosResolvidos[formData[field.fieldName]] || null}
          error={hasError}
          helperText={hasError ? "Este campo é obrigatório" : field.helpText}
          placeholder={field.placeholder}
        />
      );
    }

    const commonProps = {
      label: field.fieldLabel + (field.isRequired ? " *" : ""),
      name: field.fieldName,
      value: formData[field.fieldName] || "",
      onChange: handleChange,
      fullWidth: true,
      variant: "outlined",
      margin: "dense",
      placeholder: field.placeholder,
      helperText: hasError ? "Este campo é obrigatório" : field.helpText,
      error: hasError,
    };
    
    // Props para FormControl (sem helperText, pois é renderizado manualmente)
    const formControlProps = {
      fullWidth: true,
      variant: "outlined",
      margin: "dense",
    };

    // Garantir que fieldOptions seja sempre um array
    const getFieldOptions = () => {
      if (!field.fieldOptions) return [];
      
      // Se já é array, retornar
      if (Array.isArray(field.fieldOptions)) return field.fieldOptions;
      
      // Se é string JSON, fazer parse
      if (typeof field.fieldOptions === 'string') {
        try {
          const parsed = JSON.parse(field.fieldOptions);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error('Erro ao fazer parse de fieldOptions:', e);
          return [];
        }
      }
      
      // Se é objeto, tentar converter em array
      if (typeof field.fieldOptions === 'object') {
        return Object.values(field.fieldOptions);
      }
      
      return [];
    };

    switch (field.fieldType) {
      case "textarea":
        // Aplicar limite maior para texto longo
        const textareaMaxLength = field.validation?.maxLength || 2000;
        const textareaCurrentLength = (formData[field.fieldName] || "").length;
        const textareaIsNearLimit = textareaCurrentLength > textareaMaxLength * 0.9; // 90% do limite
        
        return (
          <TextField
            {...commonProps}
            multiline
            rows={4}
            inputProps={{
              maxLength: textareaMaxLength
            }}
            helperText={
              hasError 
                ? "Este campo é obrigatório" 
                : field.helpText 
                  ? `${field.helpText} (${textareaCurrentLength}/${textareaMaxLength})` 
                  : `${textareaCurrentLength}/${textareaMaxLength} caracteres`
            }
            FormHelperTextProps={{
              style: textareaIsNearLimit && !hasError ? { color: '#ff9800' } : undefined
            }}
          />
        );

      case "select":
        const selectOptions = getFieldOptions();
        
        // Validar se o valor atual existe nas opções
        const selectCurrentValue = formData[field.fieldName] || "";
        const isSelectValidValue = selectCurrentValue === "" || selectOptions.some(opt => String(opt.value) === String(selectCurrentValue));
        
        // Se o valor não é válido, usar string vazia
        const selectValue = isSelectValidValue ? selectCurrentValue : "";
        
        return (
          <FormControl {...formControlProps} error={hasError}>
            <InputLabel error={hasError}>{field.fieldLabel + (field.isRequired ? " *" : "")}</InputLabel>
            <Select
              name={field.fieldName}
              value={selectValue}
              onChange={handleChange}
              label={field.fieldLabel + (field.isRequired ? " *" : "")}
              error={hasError}
            >
              {selectOptions.length === 0 && (
                <MenuItem value="" disabled>
                  Nenhuma opção disponível
                </MenuItem>
              )}
              {selectOptions.map((option, index) => (
                <MenuItem key={option.value || index} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {hasError && <FormHelperText>Este campo é obrigatório</FormHelperText>}
            {!hasError && field.helpText && <FormHelperText>{field.helpText}</FormHelperText>}
          </FormControl>
        );

      case "radio":
        const radioOptions = getFieldOptions();
        const radioValue = formData[field.fieldName] || "";
        
        return (
          <FormControl component="fieldset" error={hasError} style={{ width: '100%', marginTop: '8px', marginBottom: '4px' }}>
            <FormLabel component="legend">
              {field.fieldLabel + (field.isRequired ? " *" : "")}
            </FormLabel>
            <RadioGroup
              name={field.fieldName}
              value={radioValue}
              onChange={handleChange}
            >
              {radioOptions.length === 0 && (
                <Typography variant="caption" color="textSecondary">
                  Nenhuma opção disponível
                </Typography>
              )}
              {radioOptions.map((option, index) => (
                <FormControlLabel
                  key={option.value || index}
                  value={option.value}
                  control={<Radio />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {hasError && <FormHelperText>Este campo é obrigatório</FormHelperText>}
            {!hasError && field.helpText && <FormHelperText>{field.helpText}</FormHelperText>}
          </FormControl>
        );

      case "multiselect":
        const multiselectOptions = getFieldOptions();
        const multiselectValue = Array.isArray(formData[field.fieldName]) ? formData[field.fieldName] : [];
        
        return (
          <FormControl {...formControlProps} error={hasError}>
            <InputLabel error={hasError}>{field.fieldLabel + (field.isRequired ? " *" : "")}</InputLabel>
            <Select
              name={field.fieldName}
              multiple
              value={multiselectValue}
              onChange={handleChange}
              label={field.fieldLabel + (field.isRequired ? " *" : "")}
              error={hasError}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const option = multiselectOptions.find(opt => opt.value === value);
                    return (
                      <Chip 
                        key={value} 
                        label={option?.label || value} 
                        size="small"
                        style={{ margin: '2px' }}
                      />
                    );
                  })}
                </Box>
              )}
            >
              {multiselectOptions.length === 0 && (
                <MenuItem value="" disabled>
                  Nenhuma opção disponível
                </MenuItem>
              )}
              {multiselectOptions.map((option, index) => (
                <MenuItem key={option.value || index} value={option.value}>
                  <Checkbox checked={multiselectValue.indexOf(option.value) > -1} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {hasError && <FormHelperText>Este campo é obrigatório</FormHelperText>}
            {!hasError && field.helpText && <FormHelperText>{field.helpText}</FormHelperText>}
          </FormControl>
        );

      case "checkbox":
        const checkboxOptions = getFieldOptions();
        const checkboxValues = Array.isArray(formData[field.fieldName]) ? formData[field.fieldName] : [];
        
        return (
          <FormControl component="fieldset" error={hasError} style={{ width: '100%', marginTop: '8px', marginBottom: '4px' }}>
            <FormLabel component="legend">
              {field.fieldLabel + (field.isRequired ? " *" : "")}
            </FormLabel>
            <Box style={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}>
              {checkboxOptions.length === 0 && (
                <Typography variant="caption" color="textSecondary">
                  Nenhuma opção disponível
                </Typography>
              )}
              {checkboxOptions.map((option, index) => (
                <FormControlLabel
                  key={option.value || index}
                  control={
                    <Checkbox
                      checked={checkboxValues.includes(option.value)}
                      onChange={(e) => handleCheckboxChange(field.fieldName, option.value, e.target.checked)}
                      name={field.fieldName}
                    />
                  }
                  label={option.label}
                />
              ))}
            </Box>
            {hasError && <FormHelperText>Este campo é obrigatório (selecione pelo menos uma opção)</FormHelperText>}
            {!hasError && field.helpText && <FormHelperText>{field.helpText}</FormHelperText>}
          </FormControl>
        );

      case "date": {
        // Data de devolução dos boards de locação: é derivada do tipo, então fica
        // travada. Só ESPECIAL — o único tipo sem prazo fixo — libera a digitação.
        const ehDevolucaoDeLocacao =
          field.fieldName === DATA_DEVOLUCAO_FIELD &&
          fieldDefinitions.some((f) => f.fieldName === TIPO_LOCACAO_FIELD);
        const travadaPeloTipo =
          ehDevolucaoDeLocacao && !isDevolucaoEditavel(formData[TIPO_LOCACAO_FIELD]);

        // Com ESPECIAL o campo é digitado à mão: a explicação de que ele é
        // calculado só faz sentido enquanto está travado.
        const ajudaDevolucao = travadaPeloTipo
          ? field.helpText || "Calculada pelo tipo de locação"
          : undefined;

        return (
          <TextField
            {...commonProps}
            type="date"
            disabled={travadaPeloTipo}
            helperText={
              hasError
                ? "Este campo é obrigatório"
                : ehDevolucaoDeLocacao
                  ? ajudaDevolucao
                  : field.helpText
            }
            InputLabelProps={{
              shrink: true,
            }}
          />
        );
      }

      case "number":
        return (
          <TextField
            {...commonProps}
            type="number"
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
              step: field.validation?.step || "any"
            }}
          />
        );

      case "email":
        const emailMaxLength = field.validation?.maxLength || 255;
        const emailCurrentLength = (formData[field.fieldName] || "").length;
        
        return (
          <TextField
            {...commonProps}
            type="email"
            inputProps={{
              maxLength: emailMaxLength
            }}
            helperText={
              hasError 
                ? "Este campo é obrigatório" 
                : field.helpText 
                  ? `${field.helpText} (${emailCurrentLength}/${emailMaxLength})` 
                  : `${emailCurrentLength}/${emailMaxLength} caracteres`
            }
          />
        );

      case "money":
        return (
          <MonetaryInput
            name={field.fieldName}
            label={field.fieldLabel + (field.isRequired ? " *" : "")}
            value={formData[field.fieldName] || 0}
            onChange={handleChange}
            placeholder={field.placeholder || "R$ 0,00"}
            error={hasError}
            helperText={hasError ? "Este campo é obrigatório" : field.helpText}
            required={field.isRequired}
            fullWidth
            variant="outlined"
            margin="dense"
          />
        );

      case "color":
        return (
          <Box>
            <Typography 
              variant="caption" 
              color={hasError ? "error" : "textSecondary"} 
              gutterBottom
            >
              {field.fieldLabel + (field.isRequired ? " *" : "")}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <TextField
                name={field.fieldName}
                value={formData[field.fieldName] || ""}
                onChange={handleChange}
                type="color"
                label=""
                fullWidth
                variant="outlined"
                margin="dense"
                error={hasError}
                InputLabelProps={{
                  shrink: true,
                }}
                style={{ width: '100%', minHeight: '56px' }}
                InputProps={{
                  style: { height: '56px', cursor: 'pointer' }
                }}
              />
            </Box>
            {hasError && (
              <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                Este campo é obrigatório
              </Typography>
            )}
            {!hasError && field.helpText && (
              <Typography variant="caption" color="textSecondary" style={{ marginTop: 4, display: 'block' }}>
                {field.helpText}
              </Typography>
            )}
          </Box>
        );

      case "text":
      default:
        // Aplicar limite de caracteres para texto simples
        const maxLength = field.validation?.maxLength || 255;
        const currentLength = (formData[field.fieldName] || "").length;
        const isNearLimit = currentLength > maxLength * 0.8; // 80% do limite
        
        return (
          <TextField 
            {...commonProps}
            inputProps={{
              maxLength: maxLength
            }}
            helperText={
              hasError 
                ? "Este campo é obrigatório" 
                : field.helpText 
                  ? `${field.helpText} (${currentLength}/${maxLength})` 
                  : `${currentLength}/${maxLength} caracteres`
            }
            FormHelperTextProps={{
              style: isNearLimit && !hasError ? { color: '#ff9800' } : undefined
            }}
          />
        );
    }
  };

  const handleOpenRelatedCard = (relatedCard) => {
    if (!relatedCard?.boardId) {
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.set("boardId", String(relatedCard.boardId));
    if (relatedCard.columnId) {
      searchParams.set("columnId", String(relatedCard.columnId));
    }
    searchParams.set("cardId", String(relatedCard.id));

    onClose();
    history.push(`/kanban-board?${searchParams.toString()}`);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{card ? "Editar Card" : "Novo Card"}</DialogTitle>
        <DialogContent>
          {loadingFields ? (
            <Box textAlign="center" py={4}>
              <Typography>Carregando campos...</Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {/* Campo de Cliente */}
                <Grid item xs={12}>
                  <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                    <Box flex={1}>
                      <Autocomplete
                        options={clientes}
                        filterOptions={(options) => options}
                        // Sem isso o MUI compara por referência e perde a
                        // seleção sempre que o debounce recarrega a lista
                        getOptionSelected={(option, value) =>
                          String(option?.CODCLI) === String(value?.CODCLI)
                        }
                        getOptionLabel={(option) => {
                          const nome = option.NOMEFANTASIA || option.NOMECLI || "";
                          const cidade = option.CIDADECLI ? ` - ${option.CIDADECLI}/${option.ESTADOCLI}` : "";
                          return `${nome}${cidade}`;
                        }}
                        value={selectedCliente}
                        onChange={(event, newValue) => {
                          setSelectedCliente(newValue);
                        }}
                        onInputChange={(event, newInputValue) => {
                          setClienteSearchText(newInputValue);
                        }}
                        loading={loadingClientes}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Cliente"
                            variant="outlined"
                            margin="dense"
                            placeholder="Digite para buscar um cliente..."
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingClientes ? <CircularProgress color="inherit" size={20} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(option) => (
                          <Box>
                            <Typography variant="body2">
                              <strong>{option.NOMEFANTASIA || option.NOMECLI}</strong>
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {option.NOMECLI}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              {option.CGCCLI || option.CPFCLI}{(option.CIDADECLI) ? ` · ${option.CIDADECLI}/${option.ESTADOCLI}` : ""}
                            </Typography>
                          </Box>
                        )}
                        noOptionsText="Nenhum cliente encontrado"
                      />
                    </Box>
                    <IconButton
                      color="primary"
                      onClick={() => setQuickCreateOpen(true)}
                      title="Criar novo cliente"
                      style={{ marginTop: 8 }}
                    >
                      <AddCircleOutlineIcon />
                    </IconButton>
                    {isClientImportEnabled && (
                      <IconButton
                        color="primary"
                        onClick={handleImportClientes}
                        disabled={importingClientes}
                        title="Importar/Atualizar clientes"
                        style={{ marginTop: 8 }}
                      >
                        {importingClientes ? (
                          <CircularProgress size={24} />
                        ) : (
                          <RefreshIcon />
                        )}
                      </IconButton>
                    )}
                  </Box>
                </Grid>

                {/* Sugestão de usuário responsável do cliente */}
                {responsavelSuggestion && !card && (
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      p={1}
                      borderRadius={1}
                      style={{ background: isDark ? alpha(orange[500], 0.2) : "#fff3e0", border: "1px solid #ff9800" }}
                    >
                      <Typography variant="body2" style={{ color: isDark ? orange[300] : "#e65100" }}>
                        O cliente selecionado tem <strong>{responsavelSuggestion.userName}</strong> como
                        usuário responsável. Deseja incluí-lo no campo{" "}
                        <strong>{responsavelSuggestion.fieldLabel}</strong>?
                      </Typography>
                      <Box display="flex" alignItems="center" style={{ flexShrink: 0, marginLeft: 8 }}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, userID: responsavelSuggestion.userId }));
                            setResponsavelSuggestion(null);
                          }}
                        >
                          Usar
                        </Button>
                        <IconButton size="small" onClick={() => setResponsavelSuggestion(null)} style={{ marginLeft: 4 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                )}

                {/* Campo de Usuário de Venda - Aparece apenas se existe coluna com registerSale */}
                {hasRegisterSaleColumn && (
                  <Grid item xs={12}>
                    <Autocomplete
                      options={users}
                      getOptionLabel={(option) => option.name || ""}
                      value={selectedSaleUser}
                      onChange={(event, newValue) => {
                        setSelectedSaleUser(newValue);
                      }}
                      loading={loadingUsers}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Usuário Responsável pela Venda"
                          variant="outlined"
                          margin="dense"
                          placeholder="Selecione o usuário para registrar a venda..."
                          helperText="Este usuário será usado para registrar a venda quando o card for movido para uma coluna de venda"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(option) => (
                        <Box>
                          <Typography variant="body2">
                            <strong>{option.name}</strong>
                          </Typography>
                          {option.email && (
                            <Typography variant="caption" color="textSecondary">
                              {option.email}
                            </Typography>
                          )}
                        </Box>
                      )}
                      noOptionsText="Nenhum usuário encontrado"
                    />
                  </Grid>
                )}

                {/* Campos de Agendamento - Apenas para novos cards E se configuração estiver ativa */}
                {!card && requireSchedule && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom style={{ fontWeight: 600, marginTop: 16 }}>
                        Agendamento (Obrigatório)
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Data do Agendamento *"
                        type="datetime-local"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        helperText="Informe a data e hora para o agendamento"
                      />
                    </Grid>
                  </>
                )}

                {/* Campos customizáveis do board */}
                {/* 1. Campos sempre visíveis (ordenados por fieldOrder) */}
                {fieldDefinitions
                  .filter(field => field.alwaysVisible !== false)
                  .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                  .map((field, index) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={field.fieldType === "textarea" ? 12 : 6}
                      key={field.fieldName || `visible-${index}`}
                    >
                      {renderField(field)}
                    </Grid>
                  ))
                }
                
                {/* 2. Botão "Mais Informações" - Exibir se houver campos ocultos */}
                {fieldDefinitions.some(field => field.alwaysVisible === false) && (
                  <Grid item xs={12}>
                    <Box display="flex" justifyContent="center" mt={1} mb={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={showAllFields ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        onClick={() => setShowAllFields(!showAllFields)}
                        size="small"
                      >
                        {showAllFields ? "Menos Informações" : "Mais Informações"}
                      </Button>
                    </Box>
                  </Grid>
                )}
                
                {/* 3. Campos ocultáveis (ordenados por fieldOrder) - Exibidos apenas quando showAllFields está ativo */}
                {showAllFields && fieldDefinitions
                  .filter(field => field.alwaysVisible === false)
                  .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                  .map((field, index) => (
                    <Grid 
                      item 
                      xs={12} 
                      sm={field.fieldType === "textarea" ? 12 : 6}
                      key={field.fieldName || `hidden-${index}`}
                    >
                      {renderField(field)}
                    </Grid>
                  ))
                }
              </Grid>

              {/* Seção de negociação vinculada */}
              {!!card?.id && (loadingRelatedCards || relatedNegotiationRef) && (
                <Box mt={2} mb={1}>
                  <Box display="flex" alignItems="center" gridGap={8} mb={1}>
                    <LinkIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                      Negociação vinculada
                    </Typography>
                    {/*relatedNegotiationRef && (
                      <Chip size="small" color="primary" variant="outlined" label={relatedNegotiationRef} />
                    )*/}
                  </Box>

                  {loadingRelatedCards ? (
                    <Box display="flex" alignItems="center" gridGap={8}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="textSecondary">
                        Carregando cards relacionados...
                      </Typography>
                    </Box>
                  ) : relatedCards.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      Este card ainda não possui outro card vinculado em outro fluxo.
                    </Typography>
                  ) : (
                    <List dense>
                      {relatedCards.map((relatedCard) => (
                        <ListItem
                          key={relatedCard.id}
                          button
                          onClick={() => handleOpenRelatedCard(relatedCard)}
                        >
                          <ListItemIcon>
                            <LinkIcon fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={relatedCard.title || `Card #${relatedCard.id}`}
                            secondary={`${relatedCard.board?.name || "Board"} - ${relatedCard.column?.name || "Coluna"}`}
                          />
                          <ListItemSecondaryAction>
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => handleOpenRelatedCard(relatedCard)}
                            >
                              Abrir
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {/* Seção de Comentários - com cliente mostra a timeline completa;
                  sem cliente (card salvo) permite registrar relacionamentos do atendimento */}
              {(selectedCliente?.CODCLI || card?.clienteId || card?.id) && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
                    {(selectedCliente?.CODCLI || card?.clienteId) ? "Comentários" : "Relacionamentos do atendimento"}
                  </Typography>
                  <Box
                    border={1}
                    borderColor="divider"
                    borderRadius="8px"
                    overflow="hidden"
                  >
                    <ClienteComments
                      ref={clienteCommentsRef}
                      clienteId={selectedCliente?.CODCLI || card?.clienteId || null}
                      cardId={card?.id || null}
                      contactId={card?.contactId || null}
                      contactName={card?.contact?.name || null}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="default">
            Cancelar
          </Button>
          <Button 
            type="submit" 
            color="primary" 
            disabled={loading || loadingFields}
          >
            {loading ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>

    {/* Dialog de Importação de Clientes */}
    <Dialog
        open={importDialogOpen}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" align="center">
            Importando Clientes
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box width="100%" mb={2}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="textSecondary">
                {importProgress.message}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {importProgress.percentual}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={importProgress.percentual} 
            />
          </Box>
          
          <Box textAlign="center" mt={2}>
            <CircularProgress size={40} />
            <Typography variant="caption" display="block" color="textSecondary" mt={1}>
              Por favor, aguarde...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Fora do <form> do card: portais propagam eventos React pela árvore de
          componentes, então aqui dentro o submit do cadastro rápido salvaria o
          card por baixo */}
      <ClienteQuickCreateModal
        open={quickCreateOpen}
        onClose={() => setQuickCreateOpen(false)}
        onSave={handleClienteCreated}
        // Com cliente selecionado, clienteSearchText guarda o label da seleção
        // (vem do onInputChange), não o que o usuário digitou
        initialName={selectedCliente ? "" : clienteSearchText}
      />
    </>
  );
};

export default CardModal;
